// src/components/ui/dashboard/achievements-card.tsx
"use client"

import { Star, Rocket, Brain, Globe, Flame, BookOpen, Calculator, Palette } from "lucide-react"
import { AchievementBadge } from "../achievement-badge"
import { useThemeStore } from "@/hooks/stores/themeStore"
import { StudentProfile } from "@/hooks/stores/StudentStore"

interface AchievementsGridProps {
  studentProfile?: StudentProfile | null
}

export function AchievementsGrid({ studentProfile }: AchievementsGridProps) {
  const currentTheme = useThemeStore((state) => state.getCurrentTheme())

  const hasTraitProfile =
    studentProfile?.traitProfile != null &&
    Object.keys(studentProfile.traitProfile).length > 0

  const hasBroadPaths = (studentProfile?.selectedBroadPaths?.length ?? 0) > 0

  const hasJourneyStarted = !!localStorage.getItem("journey_progress")

  const hasAcademicResults =
    ((studentProfile?.academicResults as unknown[] | undefined)?.length ?? 0) > 0

  const hasCareerInterests = (studentProfile?.careerInterests?.length ?? 0) >= 3

  const achievements = [
    {
      icon: Star,
      isUnlocked: true,
      name: "First Steps",
      description: "Joined EarlyScape",
      color: currentTheme.colors.primary,
    },
    {
      icon: Brain,
      isUnlocked: hasTraitProfile,
      name: "Self Explorer",
      description: "Completed psychometric assessment",
      color: currentTheme.colors.accent,
    },
    {
      icon: Rocket,
      isUnlocked: hasBroadPaths,
      name: "Path Finder",
      description: "Selected your first career path",
      color: currentTheme.colors.secondary,
    },
    {
      icon: BookOpen,
      isUnlocked: hasJourneyStarted,
      name: "Journey Begun",
      description: "Started your learning journey",
      color: currentTheme.colors.primary,
    },
    {
      icon: Globe,
      isUnlocked: hasCareerInterests,
      name: "Career Explorer",
      description: "Explored 3+ career paths",
      color: currentTheme.colors.secondary,
    },
    {
      icon: Calculator,
      isUnlocked: hasAcademicResults,
      name: "Scholar",
      description: "Uploaded academic results",
      color: currentTheme.colors.primary,
    },
    {
      icon: Flame,
      isUnlocked: hasTraitProfile && hasBroadPaths && hasJourneyStarted,
      name: "On Fire",
      description: "Assessment done, path chosen, journey started",
      color: "#EF4444",
    },
    {
      icon: Palette,
      isUnlocked: (studentProfile?.metadata?.profileCompleteness ?? 0) >= 80,
      name: "Profile Complete",
      description: "Profile completeness reached 80%",
      color: currentTheme.colors.accent,
    },
  ]

  const unlockedCount = achievements.filter((a) => a.isUnlocked).length

  return (
    <div
      className="p-6 rounded-2xl"
      style={{
        backgroundColor: "var(--theme-card)",
        border: `1px solid ${currentTheme.colors.border}`,
      }}
    >
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold" style={{ color: "var(--theme-foreground)" }}>
          Achievements
        </h3>
        <p className="text-sm" style={{ color: currentTheme.colors.primary }}>
          {unlockedCount}/{achievements.length} Unlocked
        </p>
      </div>
      <div className="grid grid-cols-4 gap-4">
        {achievements.map((achievement, index) => (
          <AchievementBadge
            key={index}
            {...achievement}
            themeColors={currentTheme.colors}
          />
        ))}
      </div>
    </div>
  )
}
